import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { AddItemsWishlistNotificationSuccessEvent, AddItemsWishlistSuccessAction, DeleteItemWishlistSuccessAction } from '../state/wishlist-events.actions';

import { CartItem } from '@api/cart/cart.models';
import { getPage } from '@core/infrastructure/state/core.selectors';
import {
    PublicSidebarCustomCommandProps,
    RegisterSidebarCustomComponent,
    SidebarActionTypes,
    SidebarPublicPort,
} from '@features/sidebar';
import { SidebarView } from '@features/sidebar/domain/models/sidebar.models';
import { viewWish } from '@features/sidebar/infrastructure/state/sidebar.selectors';
import { WishlistPublicActions } from '@features/wishlist';
import { WLItem } from '@features/wishlist/domain/models/wishlist.models';
import { NotificationService, PublicNotificationTypeEnum } from '@layout/ui/components/notification';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { MINI_CART_COMPONENT_NAME } from '@shared/shopcart/ui/containers/mini-cart-sidebar/mini-cart-sidebar.constants';
import { selectUserIsLogged } from '@shared/user/store';

const DIALOG_LIFETIME = 3000;

/**
 * Get the notification action to be executed when the user is in a page different from the wishlist or shopcart
 *
 * @param store The store
 * @param currentPage The current page
 * @returns The notification action callback
 */
function getNotificationActionForAdditions(store: Store, currentPage: string): () => void {
    return currentPage !== 'ItxAngularWishlistPage' && currentPage !== 'ItxAngularShopcartPage'
        ? () => {
            RegisterSidebarCustomComponent(
                MINI_CART_COMPONENT_NAME,
                () => import('../../../../shared/shopcart/ui/containers/mini-cart-sidebar/mini-cart-sidebar.component').then(
                    (m) => m.MiniCartSidebarComponent
                )
            );

            store.dispatch(SidebarActionTypes.ShowMiniWish());

            const props: PublicSidebarCustomCommandProps = {
                component: MINI_CART_COMPONENT_NAME,
                initializeFunction: undefined,
                data: {},
                view: SidebarView.CUSTOM_NEW,
            };

            SidebarPublicPort(store).openSidebarCustom(props);
        } : undefined;
}

/**
 * Get the notification action to be executed when the user is in a page different from the wishlist or shopcart
 * and the item is deleted from the wishlist
 *
 * @param store The store
 * @param currentPage The current page
 * @param deletedItem The item just deleted
 * @param isWish If the user is viewing the mini wishlist
 * @returns The notification action callback
 */
function getNotificationActionForDeletions(
    store: Store,
    currentPage: string,
    deletedItem: WLItem,
    isWish: boolean,
): () => void {
    let action: () => void;

    switch (currentPage) {
        case 'ItxAngularWishlistPage':
            action = () => {
                store.dispatch(WishlistPublicActions.AddItemsWishlistAction(
                    {
                        items: [{
                            item: {
                                catentryId: deletedItem.catentryId,
                                id: deletedItem.productId,
                            } as unknown as CartItem,
                            sku: Number(deletedItem.catentryId),
                        }],
                        deleteItemCart: false,
                        config: { eventFrom: EventFrom.wishList },
                    }
                ));
            };
            break;
        case 'ItxAngularShopcartPage':
            break;
        default:
            if (!isWish) {
                action = () => {
                    // NOTE: esta parte de código no cuenta como cubierta usando expect.any(Function) en el test
                    RegisterSidebarCustomComponent(
                        MINI_CART_COMPONENT_NAME,
                        () => import('../../../../shared/shopcart/ui/containers/mini-cart-sidebar/mini-cart-sidebar.component').then(
                            (m) => m.MiniCartSidebarComponent
                        )
                    );

                    store.dispatch(SidebarActionTypes.ShowMiniWish());

                    const props: PublicSidebarCustomCommandProps = {
                        component: MINI_CART_COMPONENT_NAME,
                        initializeFunction: undefined,
                        data: {},
                        view: SidebarView.CUSTOM_NEW,
                    };

                    SidebarPublicPort(store).openSidebarCustom(props);
                };
            }
    }
    return action;
}

export const notifyItemsAddedToWishlistSuccessfully$ = createEffect(
    (
        actions$: Actions = inject(Actions),
        store: Store = inject(Store),
        notificationService: NotificationService = inject(NotificationService),
    ) => actions$.pipe(
        ofType(AddItemsWishlistSuccessAction),
        concatLatestFrom(() => [
            store.select(selectUserIsLogged),
            store.select(getPage),
        ]),
        filter(([_, userIsLogged, currentPage]) => currentPage !== 'ItxAngularWishlistPage' && userIsLogged),
        map(([_payload, _userIsLogged, currentPage]) => {
            const action = getNotificationActionForAdditions(store, currentPage);

            notificationService.openNotification({
                title: 'dev.product.Saved',
                type: PublicNotificationTypeEnum.TOAST,
                message: 'dev.product.ViewWishlist',
                time: DIALOG_LIFETIME,
                action,
            });

            return AddItemsWishlistNotificationSuccessEvent();
        })
    ),
    {
        functional: true,
    }
);

export const notifyItemsRemovedToWishlistSuccessfully$ = createEffect(
    (
        actions$: Actions = inject(Actions),
        store: Store = inject(Store),
        notificationService: NotificationService = inject(NotificationService),
    ) => actions$.pipe(
        ofType(DeleteItemWishlistSuccessAction),
        concatLatestFrom(() => [
            store.select(selectUserIsLogged),
            store.select(getPage),
            store.select(viewWish),
        ]),
        filter(([{ origin }, userIsLogged]) => origin === 'onlyDeleteItemWishlist' && userIsLogged),
        map(([{ deletedItem }, _userIsLogged, currentPage, isWish]) => {
            const action = getNotificationActionForDeletions(store, currentPage, deletedItem, isWish);

            let message = '';
            if (currentPage === 'ItxAngularWishlistPage') {
                message = 'dev.cart.wishlist.undo';
            } else if (!isWish) {
                message = 'dev.product.ViewWishlist';
            }

            notificationService.openNotification({
                title: 'dev.product.Removed',
                type: PublicNotificationTypeEnum.TOAST,
                message,
                time: DIALOG_LIFETIME,
                action,
            });

            return AddItemsWishlistNotificationSuccessEvent();
        })
    ),
    {
        functional: true,
    }
);
